import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import * as XLSX from 'xlsx';
import { LoadPanel } from 'devextreme-react/load-panel';
import DataGrid, { Column, Paging, ColumnFixing } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react/tooltip';
import AntConfig from '../../utils/AntConfig';
import AntLocalization from '../../utils/AntLocalization';
import AntLocalStorage from '../../utils/AntLocalStorage';

import { AntGlobal, AntSession } from '../../utils/AntContext';
import AntTime from '../../utils/AntTime';
import stringify from '../../utils/stableStringify';
import { applyMappedImportEmptyPolicy, buildMappedImportRow, isMappedImportField } from './AntImportRows';

const basedate = new Date(1899, 11, 30, 0, 0, 0);
const dnthresh = basedate.getTime() + (new Date().getTimezoneOffset() - basedate.getTimezoneOffset()) * 60000;

const day_ms = 24 * 60 * 60 * 1000;
const days_1462_ms = 1462 * day_ms;

const datenum = (v, date1904) => {
    let epoch = v.getTime();
    if (date1904) {
        epoch -= days_1462_ms;
    }
    return (epoch - dnthresh) / day_ms;
};

const parseDDMMYYYY = (str) => {
    if (typeof str !== 'string') return null;

    const parts = str.split('/');
    if (parts.length !== 3) return null;

    let [d, m, y] = parts.map((p) => parseInt(p, 10));

    // validate đơn giản
    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;

    return new Date(y, m - 1, d);
};
const parseDDMMYY = (str) => {
    if (typeof str !== 'string') return null;

    const parts = str.split('/');
    if (parts.length !== 3) return null;
    // Năm 4 chữ số để parseDDMMYYYY xử lý (tránh 2026 → 3926)
    if (parts[2].trim().length > 2) return null;

    let [d, m, y] = parts.map((p) => parseInt(p, 10));

    // validate đơn giản
    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;

    // chuyển 2 chữ số năm thành 4 chữ số
    if (y < 50) {
        y = 2000 + y;
    } else {
        y = 1900 + y;
    }

    return new Date(y, m - 1, d);
};

const fixImportedDate = (date, isDate1904) => {
    if (typeof date === 'string') {
        let parsedStr = parseDDMMYY(date);
        if (!parsedStr) parsedStr = parseDDMMYYYY(date);
        if (!parsedStr) return date; // throw new Error('Invalid date string: ' + date);
        date = parsedStr;
    }
    try {
        const parsed = XLSX.SSF.parse_date_code(datenum(date, isDate1904), { date1904: isDate1904 });

        return new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H, parsed.M, parsed.S);
    } catch (error) { }

    return date;
};

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const [gridData, setGridData] = useState({ rows: [], cols: [] });

    const getDataRows = () => {
        return gridData ? gridData.rows : [];
    };

    const load = async () => {
        setLoading(true);
        var cols = [];

        for (let i = 0; i < props.mappedFields.length; i++) {
            var col = props.mappedFields[i];
            if (isMappedImportField(col))
                cols.push({
                    name: col.field_name,
                    caption: col.field_label,
                    dataField: col.field_name,
                    alignment: col.alignment,
                    width: col.width,
                    fixed: col.fixed
                });
        }

        var rows = [];

        for (let i = 0; i < props.fileRows.length; i++) {
            var row = props.fileRows[i];
            var obj = buildMappedImportRow(row, props.mappedFields, i, props.importMode);

            obj.errors = await onClientValidation(obj, props.importMode);
            rows.push(obj);
        }

        rows = await onServerValidation(rows);
        rows = rows.map((row) => {
            return applyMappedImportEmptyPolicy(
                row,
                props.fileRows[row.row_index],
                props.mappedFields,
                props.importMode,
                row.activity
            );
        });

        setLoading(false);
        setGridData({
            cols: cols,
            rows: rows
        });
    };

    const onClientValidation = async (obj) => {
        var errors = [];

        if (gridData && gridData.cols instanceof Array) {
            for (let i = 0; i < gridData.cols.length; i++) {
                const col = gridData.cols[i];
                const e = config.getCnfField(col.dataField, tableName);
                if (e) {
                    var eLabel = AntLocalization.translateText(e.label);

                    var rules = [];
                    if (e.clientRules && e.clientRules instanceof Array)
                        rules = rules.concat(
                            e.clientRules.map((r) => {
                                return { ...r, validationCallback: undefined };
                            })
                        );
                    if (e.serverRules && e.serverRules instanceof Array)
                        rules = rules.concat(
                            e.serverRules.map((r) => {
                                return { ...r, validationCallback: undefined };
                            })
                        );

                    for (let j = 0; j < rules.length; j++) {
                        const rule = rules[j];

                        if (rule.type === 'required' && (obj[e.field] == null || obj[e.field] == undefined || String(obj[e.field]).trim() === '')) {
                            errors.push({
                                fieldName: e.field,
                                error: `${eLabel} ${String(
                                    rule.message ? rule.message : AntLocalization.translateText({ vi: 'không được trống', en: 'cannot be empty', fr: 'ne peut être vide', es: 'no puede estar vacío', id: 'tidak boleh kosong', ja: '空にできません', zh: '不能为空', ko: '비워둘 수 없습니다', hi: 'खाली नहीं हो सकता', ar: 'لا يكون فارغاً' })
                                ).toLowerCase()}`
                            });
                            break;
                        }

                        if (
                            rule.type === 'numberOnly' &&
                            obj[e.field] !== null &&
                            obj[e.field] !== undefined &&
                            String(obj[e.field]).trim() !== '' &&
                            !new RegExp(/^\d+$/).test(String(obj[e.field]))
                        ) {
                            errors.push({
                                fieldName: e.field,
                                error: `${eLabel} ${String(
                                    rule.message ? rule.message : AntLocalization.translateText({ vi: 'chỉ được nhập số', en: 'numberic only', fr: 'numérique uniquement', es: 'solo numérico', id: 'hanya angka', ja: '数字のみ', zh: '仅限数字', ko: '숫자만', hi: 'केवल संख्या', ar: 'أرقام فقط' })
                                ).toLowerCase()}`
                            });
                            break;
                        }

                        if (
                            rule.type === 'withoutSpace' &&
                            obj[e.field] !== null &&
                            obj[e.field] !== undefined &&
                            String(obj[e.field]).trim() !== '' &&
                            !new RegExp(/^[^\s]+$/).test(String(obj[e.field]))
                        ) {
                            errors.push({
                                fieldName: e.field,
                                error: `${eLabel} ${String(
                                    rule.message
                                        ? rule.message
                                        : AntLocalization.translateText({ vi: 'không được nhập khoảng trắng', en: 'include spacing character', fr: 'contient un caractère d\'espacement', es: 'incluye caracteres de espacio', id: 'mengandung karakter spasi', ja: 'スペース文字を含む', zh: '包含空格字符', ko: '공백 문자 포함', hi: 'रिक्ति वर्ण शामिल', ar: 'يتضمن حرف مسافة' })
                                ).toLowerCase()}`
                            });
                            break;
                        }

                        if (
                            rule.type === 'withoutSpecialKeys' &&
                            obj[e.field] !== null &&
                            obj[e.field] !== undefined &&
                            String(obj[e.field]).trim() !== '' &&
                            !new RegExp(rule.pattern).test(String(obj[e.field]))
                        ) {
                            errors.push({
                                fieldName: e.field,
                                error: `${eLabel} ${String(
                                    rule.message
                                        ? rule.message
                                        : AntLocalization.translateText({ vi: 'chứa ký tự đặc biệt', en: 'has special characters', fr: 'a des caractères spéciaux', es: 'incluye caracteres especiales', id: 'mengandung karakter khusus', ja: '特殊文字を含む', zh: '包含特殊字符', ko: '특수 문자 포함', hi: 'विशेष वर्ण शामिल', ar: 'يتضمن أحرف خاصة' })
                                ).toLowerCase()}`
                            });
                            break;
                        }

                        if (
                            rule.type === 'pattern' &&
                            obj[e.field] !== null &&
                            obj[e.field] !== undefined &&
                            String(obj[e.field]).trim() !== '' &&
                            !new RegExp(rule.pattern).test(String(obj[e.field]))
                        ) {
                            errors.push({
                                fieldName: e.field,
                                error: `${eLabel} ${String(rule.message ? rule.message : 'không hợp lệ').toLowerCase()}`
                            });
                            break;
                        }

                        if (
                            rule.type === 'stringLength' &&
                            obj[e.field] !== null &&
                            obj[e.field] !== undefined &&
                            rule.max &&
                            String(obj[e.field]).length > rule.max
                        ) {
                            errors.push({
                                fieldName: e.field,
                                error: `${eLabel} ${String(
                                    rule.message
                                        ? rule.message
                                        : `${AntLocalization.translateText({ vi: 'tối đa', en: 'maximum', fr: 'maximum', es: 'máximo', id: 'maksimum', ja: '最大', zh: '最多', ko: '최대', hi: 'अधिकतम', ar: 'الحد الأقصى' })} ${rule.max
                                        } ${AntLocalization.translateText({ vi: 'ký tự', en: 'characters', fr: 'caractères', es: 'caracteres', id: 'karakter', ja: '文字', zh: '个字符', ko: '자', hi: 'वर्ण', ar: 'أحرف' })}`
                                ).toLowerCase()}`
                            });
                            break;
                        }

                        if (rule.allowInImport && (rule.type === 'async' || rule.type === 'must-existed' || rule.type === 'must-not-existed')) {
                            var isInValid = false;

                            // try {
                            //     if (rule.validationCallback) isInValid = !(await rule.validationCallback(obj, props.importMode));
                            //     else

                            //     if (obj[e.field] !== null && obj[e.field] !== undefined) {
                            //         var tmpObj = {};
                            //         tmpObj['check_' + e.field] = true;
                            //         tmpObj['new_' + e.field] = obj[e.field];
                            //         tmpObj.props.importMode = props.importMode;

                            //         if (props.API) {
                            //             var result = await props.API.postValidation(tmpObj);
                            //             isInValid = !result || !result.isValid;
                            //         }
                            //     }
                            // } catch (error) {}

                            if (rule.type === 'must-not-existed') rule.message = AntLocalization.translateText({ vi: 'Đã tồn tại', en: 'Existed', fr: 'Existe déjà', es: 'Ya existe', id: 'Sudah ada', ja: '既に存在', zh: '已存在', ko: '이미 존재함', hi: 'पहले से मौजूद', ar: 'موجود بالفعل' });
                            if (rule.type === 'must-existed')
                                rule.message = AntLocalization.translateText({ vi: 'Không tồn tại', en: 'Not Existed', fr: 'N\'existe pas', es: 'No existe', id: 'Tidak ada', ja: '存在しません', zh: '不存在', ko: '存在하지 않음', hi: 'मौजूद नहीं', ar: 'غير موجود' });

                            if (isInValid) {
                                errors.push({
                                    fieldName: e.field,
                                    error: `${eLabel} ${rule.message ? String(rule.message).toLowerCase() : ''}`
                                });
                                break;
                            }
                        }
                    }
                }
            }
        }

        if (props.onClientValidation) {
            var ar = await props.onClientValidation(obj, props.importMode);
            if (ar && ar instanceof Array) errors.push(...ar);
        }
        return errors;
    };

    const onServerValidation = async (data) => {
        var objs = data
            .filter((row) => !row.errors || row.errors.length <= 0)
            .map((row) => {
                return {
                    ...row,
                    errors: undefined
                };
            });

        if (props.defaultObj)
            objs = objs.map((row) => {
                return {
                    ...row,
                    ...props.defaultObj
                };
            });

        objs = props.onValidateCustomData ? props.onValidateCustomData(objs) : objs;

        var objs2 = objs.map((row) => {
            Object.keys(row).forEach((k) => {
                if (row[k] instanceof Date || (typeof row[k] === 'string' && fixImportedDate(row[k], props.isDate1904))) {
                    row[k] = fixImportedDate(row[k], props.isDate1904);
                }

                if (row[k] instanceof Date) {
                    row[k] = AntTime.init(row[k]).unix();
                }
            });
            return {
                ...row,
                errors: undefined
            };
        });

        var ars = [];

        while (objs2.length) {
            ars.push(objs2.splice(0, 100));
        }

        var results = await Promise.allSettled(
            ars.map((ar) => {
                return new Promise(async (done) => {
                    var result = await props.API.postImportValidation(ar, props.importMode);
                    done(result || {});
                });
            })
        );
        results = results.filter((i) => i.status === 'fulfilled').map((i) => i.value);
        var newRows = results.map((result) => result.rows || []).flat();

        for (let i = 0; i < data.length; i++) {
            if (!data[i].errors) data[i].errors = [];

            for (let j = 0; j < newRows.length; j++) {
                if (newRows[j].row_index == data[i].row_index) {
                    data[i].activity = newRows[j].activity;

                    if (newRows[j].errors && newRows[j].errors.length > 0) data[i].errors = data[i].errors.concat(newRows[j].errors);
                }
            }
        }

        // Backend kiểm theo lô 100 dòng gửi song song nên lô nào cũng trả cùng số chỗ
        // còn lại. Gom lại ở đây: giữ đủ `remaining` dòng tạo mới đầu tiên, đánh lỗi
        // các dòng tạo mới sau đó. Dòng cập nhật không bao giờ bị đánh lỗi hạn mức.
        var quota = results.map((result) => result.quota).find((item) => item && item.remaining !== null && item.remaining !== undefined);
        if (quota) {
            var creatable = data
                .filter((row) => row.activity === 'Create' && row.errors.length === 0)
                .sort((a, b) => a.row_index - b.row_index);
            var remaining = Math.max(0, Number(quota.remaining) || 0);
            if (creatable.length > remaining) {
                var quotaError = quota.content || AntLocalization.translateText({
                    vi: 'Vượt hạn mức gói dịch vụ',
                    en: 'Exceeds your plan limit',
                    fr: 'Dépasse la limite de votre offre',
                    es: 'Supera el límite de su plan',
                    id: 'Melebihi batas paket Anda',
                    ja: 'プランの上限を超えています',
                    zh: '超出套餐限额',
                    ko: '요금제 한도를 초과합니다',
                    hi: 'आपकी योजना की सीमा से अधिक है',
                    ar: 'يتجاوز حد باقتك'
                });
                creatable.slice(remaining).forEach((row) => {
                    row.errors = row.errors.concat([{ error: quotaError }]);
                });
            }
        }

        return data;
    };

    const validate = () => {
        if (!gridData || gridData.rows.length <= 0) return false;

        return {
            length: gridData.rows.length,
            hasError: gridData.rows.some((row) => row.errors && row.errors.length > 0)
        };
    };

    const getAllErrorItems = () => {
        if (!gridData || gridData.rows.length <= 0) return false;

        var errorRows = gridData.rows.filter((row) => {
            return row.errors && row.errors.length > 0;
        });
        return errorRows;
    };

    const removeAllErrorItems = () => {
        if (!gridData || gridData.rows.length <= 0) return 0;

        var newData = gridData.rows.filter((row) => {
            return !row.errors || row.errors.length <= 0;
        });

        setGridData((d) => {
            return {
                cols: d.cols,
                rows: newData
            };
        });

        if (props.onDataChanged) props.onDataChanged(newData);

        return newData.length;
    };

    const onRemove = (data, e) => {
        if (!gridData || gridData.rows.length <= 0) return;

        var newData = gridData.rows.filter((row) => {
            return row.row_index != data.row_index;
        });

        setGridData((d) => {
            return {
                cols: d.cols,
                rows: newData
            };
        });

        if (props.onDataChanged) props.onDataChanged(newData);
    };

    const renderErrorIcon = (info) => {
        if (!info.data.errors || info.data.errors.length <= 0)
            return (
                <div key={id}>
                    <Button
                        className="ant-grid-icon ant-icon-valid"
                        id={id}
                        type="normal"
                        stylingMode="text"
                        focusStateEnabled={false}
                        hoverStateEnabled={false}
                        activeStateEnabled={false}
                    >
                        <i className={'dx-icon dx-icon-check'}></i>
                    </Button>
                </div>
            );

        var errors = info.data.errors
            .map(function (e) {
                return e.error;
            })
            .join('\n');
        errors = errors.split('\n');

        var id = 'ant-grid--error-' + info.data.row_index;

        return (
            <div key={id}>
                <Button
                    className="ant-grid-icon ant-icon-error"
                    id={id}
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                    activeStateEnabled={false}
                >
                    <i className={'dx-icon dx-icon-info'}></i>
                </Button>
                <Tooltip
                    target={`#${id}`}
                    className="ant-tooltip"
                    hideEvent="mouseleave"
                    showEvent={{
                        name: 'mouseenter dxclick keyup'
                    }}
                    hideOnOutsideClick={true}
                >
                    <div>
                        {errors.map((item, key) => {
                            if (key < errors.length - 1)
                                return (
                                    <React.Fragment>
                                        <p key={key}>{item}</p>
                                        <br key={key} />
                                    </React.Fragment>
                                );
                            else return <p key={key}>{item}</p>;
                        })}
                    </div>
                </Tooltip>
            </div>
        );
    };

    const renderRemoveButton = (info) => {
        var id = 'ant-grid-icon-' + info.data.row_index;

        return (
            <div>
                <Button
                    className="ant-grid-icon"
                    id={id}
                    onClick={(e) => {
                        onRemove(info.data, e);
                    }}
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                >
                    <i className={'dx-icon icon dx-icon-close'}></i>
                </Button>
            </div>
        );
    };

    const grid = useRef();
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        validate: validate,
        removeAllErrorItems: removeAllErrorItems,
        getDataRows: getDataRows
    }));

    useEffect(() => {
        if (props.visible && props.mappedFields.length > 0) load();
    }, [props.visible]);

    useEffect(() => {
        if (gridData) if (props.onLoaded) props.onLoaded(gridData);
    }, [gridData]);

    let errosRows = getAllErrorItems();

    let dataRows = [];
    if (gridData) dataRows = errosRows.length > 0 ? errosRows : gridData.rows;

    return (
        <React.Fragment>
            {loading && (
                <div>
                    <LoadPanel
                        position={{ of: '#ant-import' }}
                        visible={true}
                        showPane={false}
                        shading={true}
                        // shadingColor={'rgba(119, 119, 128, 0.16)'}
                        wrapperAttr={{
                            class: 'ant-loading'
                        }}
                    // indicatorSrc={'https://image.flaticon.com/icons/svg/3305/3305803.svg'}
                    />
                </div>
            )}

            {!loading && (
                <DataGrid
                    ref={grid}
                    noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
                    className={'dx-card wide-card'}
                    dataSource={dataRows}
                    keyExpr="row_index"
                    focusedRowEnabled={false}
                    columnAutoWidth={true}
                    columnHidingEnabled={false}
                    showColumnLines={false}
                    showRowLines={true}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    repaintChangesOnly={true}
                    visible={props.visible}
                    height={'100%'}
                    pager={{
                        showInfo: true,
                        infoText: `{2} ${AntLocalization.translateText({ vi: 'dòng', en: 'records', fr: 'lignes', es: 'registros', id: 'catatan', ja: 'レコード', zh: '条记录', ko: '개 레코드', hi: 'रिकॉर्ड', ar: 'سجلات' })}`,

                        visible: true
                    }}
                    sorting={{
                        clearText: AntLocalization.translateText({ vi: 'Bỏ sắp xếp', en: 'Remove sorting', fr: 'Supprimer le tri', es: 'Eliminar orden', id: 'Hapus pengurutan', ja: '並べ替えを解除', zh: '取消排序', ko: '정렬 해제', hi: 'क्रमबद्धता हटाएं', ar: 'إزالة الترتيب' }),

                        ascendingText: 'A->Z',
                        descendingText: 'Z->A'
                    }}
                    grouping={{
                        autoExpandAll: true,
                        expandMode: 'rowClick',
                        texts: {
                            groupContinuedMessage: '',
                            groupContinuesMessage: ''
                        }
                    }}
                    summary={{
                        groupItems: [
                            {
                                column: 'activity',
                                summaryType: 'count',
                                displayFormat: `{0} ${AntLocalization.translateText({ vi: 'dòng', en: 'records', fr: 'lignes', es: 'registros', id: 'catatan', ja: 'レコード', zh: '条记录', ko: '개 레코드', hi: 'रिकॉर्ड', ar: 'سجلات' })}`,

                                showInColumn: 'error'
                            }
                        ]
                    }}
                >
                    <Paging defaultPageSize={props.defaultPageSize ? props.defaultPageSize : 8} />
                    <ColumnFixing enabled={true} />
                    {props.editable && (
                        <Column
                            name="remove"
                            caption=" "
                            width={40}
                            alignment="center"
                            cellRender={renderRemoveButton}
                            fixed={true}
                            fixedPosition="left"
                        />
                    )}

                    <Column
                        name="row_index"
                        dataField="row_index"
                        caption="#"
                        width={30}
                        alignment="center"
                        fixed={true}
                        fixedPosition="left"
                        calculateCellValue={(data) => {
                            return parseInt(data.row_index) + 1;
                        }}
                    />
                    <Column name="error" caption=" " width={30} alignment="center" cellRender={renderErrorIcon} fixed={true} fixedPosition="left" />

                    {['Create', 'Update'].includes(props.importMode) === false && (
                        <Column
                            name="activity"
                            dataField="activity"
                            caption={AntLocalization.translateText({ vi: 'Tác vụ', en: 'Activity', fr: 'Activité', es: 'Actividad', id: 'Aktivitas', ja: '操作', zh: '活动', ko: '활동', hi: 'गतिविधि', ar: 'نشاط' })}
                            fixed={true}
                            fixedPosition="left"
                            // width={80}
                            alignment="left"
                            calculateCellValue={(data) => {
                                if (data.activity == 'Create') return AntLocalization.translateText({ vi: 'Tạo mới', en: 'Create New', fr: 'Créer nouveau', es: 'Crear nuevo', id: 'Buat Baru', ja: '新規作成', zh: '新建', ko: '새로 만들기', hi: 'नया बनाएं', ar: 'إنشاء جديد' });
                                if (data.activity == 'Update') return AntLocalization.translateText({ vi: 'Cập nhật', en: 'Update', fr: 'Mettre à jour', es: 'Actualizar', id: 'Perbarui', ja: '更新', zh: '更新', ko: '업데이트', hi: 'अपडेट करें', ar: 'تحديث' });
                                return data.activity;
                            }}
                            groupIndex={0}
                        />
                    )}

                    {gridData &&
                        gridData.cols
                            .filter((col) => {
                                return col.dataField && gridData.rows.find((r) => r[col.dataField] !== undefined && r[col.dataField] !== '');
                            })
                            .map((col, i) => {
                                col.editorOptions = col.editorOptions ? col.editorOptions : {};

                                let cfnField = config.getCnfField(col.dataField, tableName);

                                if (cfnField) {
                                    let colType = cfnField.dataType;
                                    // col.dataType = colType;

                                    if (colType === 'currency') {
                                        col.format = AntLocalization.getCurrencyFormat();
                                    }

                                    // if (colType === 'int' || colType === 'fixedPoint') col.format = '#,##0';

                                    // if (colType === 'float' || colType === 'decimal') {
                                    //     var decimalDigit = found ? found.decimalDigit : undefined;
                                    //     if (decimalDigit === undefined || decimalDigit === null)
                                    //         decimalDigit = AntLocalization.getCurrencyDecimalDigit();

                                    //     if (decimalDigit === undefined || decimalDigit === null) decimalDigit = 2;
                                    //     col.format = '#,##0.' + String('#').repeat(decimalDigit);
                                    // }

                                    // if (colType === 'percent') {
                                    //     var decimalDigit = found ? found.decimalDigit : undefined;
                                    //     // if (decimalDigit === undefined || decimalDigit === null) decimalDigit = AntLocalization.getCurrencyDecimalDigit();
                                    //     if (decimalDigit === undefined || decimalDigit === null) decimalDigit = 0;
                                    //     col.editorOptions.format = {
                                    //         type: 'percent',
                                    //         precision: decimalDigit
                                    //     };
                                    // }
                                }

                                return (
                                    <Column
                                        key={col.dataField}
                                        name={col.name}
                                        caption={col.caption}
                                        dataField={col.dataField}
                                        // dataType={col.dataType}
                                        width={col.width}
                                        alignment={col.alignment ? col.alignment : 'left'}
                                        fixed={col.fixed}
                                        format={col.format}
                                    />
                                );
                            })}
                </DataGrid>
            )}
        </React.Fragment>
    );
});
